import { createFileRoute, Link } from '@tanstack/react-router'
import { css } from '../../../../../styled-system/css'
import { button } from '../../../../styles/buttonStyles'
import { contentWrapper } from '../../../../styles/contentWrapper'
import { titleWrapper, pageTitle, sectionWrapper, sectionTitle } from '../../../../styles/sectionStyles'
import { textLabel, text } from '../../../../styles/textStyles'
import { table, tableHead, tableHeadCell, tableRow, tableCell } from '../../../../styles/tableStyles'
import ArrowBack from '~icons/material-symbols/arrow-back.svg'
import ArrowForward from '~icons/material-symbols/arrow-forward.svg'
import Add from '~icons/material-symbols/add.svg'
import { useGetNotificationHistories, useGetUser } from '../../../../libs/admin/hooks'
import { formatTimestampJaJst } from '../../../../libs/utils/format-timestamp'
import { LoadingOverlay } from '../../../../components/loading-overlay'
import { formatGender } from '../../../../libs/utils/format-gender'
import type { NotificationHistory } from 'schema/gen/es/chiikipoint/model/v2/model_pb'
import { PointHistory } from './-components/PointHistory'


export const Route = createFileRoute('/admin/users/$subscriptionOrUserId/')({
  component: RouteComponent,
})

function RouteComponent() {
  return <Layout />
}

export const useUserInfo = () => {
  const { subscriptionOrUserId } = Route.useParams()
  const { data, isLoading: isUserLoading } = useGetUser({ subscriptionOrUserId })
  const user = data;
  const userId = user?.user?.id ?? undefined;
  const { data: notificationHistory, isLoading: isLoadingNotificationHistory } = useGetNotificationHistories({ userId })
  return {
    user,
    notificationHistory,
    isLoading: isUserLoading || isLoadingNotificationHistory
  }
}

type UserInfo = ReturnType<typeof useUserInfo>
type User = UserInfo['user']

const BasicInfo = ({ user, isLoading }: { user: User, isLoading: boolean }) => {
  return (
    <div className={sectionWrapper()}>
      {isLoading && (
        <LoadingOverlay />
      )}
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <div>
          <div className={textLabel()}>氏名</div>
          <div className={text()}>{user?.user?.name || "\u00a0"}</div>
        </div>
        <div>
          <div className={textLabel()}>性別</div>
          <div className={text()}>{user?.user?.gender ? formatGender(user?.user?.gender) : "\u00a0"}</div>
        </div>
        <div>
          <div className={textLabel()}>市区町村</div>
          <div className={text()}>{user?.user?.cityAddress || "\u00a0"}</div>
        </div>
        <div>
          <div className={textLabel()}>生年月日</div>
          <div className={text()}>{user?.user?.birthday ? formatTimestampJaJst(user.user.birthday) : "\u00a0"}</div>
        </div>
      </div>
    </div>
  )
}

export const SystemInfo = ({ user, isLoading }: { user: User, isLoading: boolean }) => {
  return (
    <div className={sectionWrapper()}>
      {isLoading && (
        <LoadingOverlay />
      )}
      <div>
        <div className={textLabel()}>サブスクリプションID</div>
        <div className={text()}>{user?.user?.subscriptionId || "\u00a0"}</div>
      </div>
      <div>
        <div className={textLabel()}>ユーザーID</div>
        <div className={text()}>{user?.user?.id || "\u00a0"}</div>
      </div>
      <div>
        <div className={textLabel()}>登録日時</div>
        <div className={text()}>{user?.user?.createdAt ? formatTimestampJaJst(user?.user?.createdAt) : "\u00a0"}</div>
      </div>
    </div>
  )
}

const NotificationHistoryRow = ({ history, subscriptionOrUserId }: { history: NotificationHistory, subscriptionOrUserId: string }) => {
  return (
    <tr className={tableRow()} key={history.id}>
      <td className={tableCell()}>{history.original?.titleTemplate}</td>
      <td className={tableCell()}>{history.original?.messageTemplate}</td>
      <td className={tableCell()}>{history.createdAt ? formatTimestampJaJst(history.createdAt) : "--"}</td>
      <td className={tableCell()}>
        <div className={css({
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
        })}>
          <Link
            to='/admin/users/$subscriptionOrUserId/notifications/$id'
            params={{ subscriptionOrUserId, id: history.id }}
          >
            <button type='button' className={button()}>
              詳細
              <ArrowForward />
            </button>
          </Link>
        </div>
      </td>
    </tr>
  )
}

const NotificationHistoryTable = ({
  subscriptionOrUserId,
  notificationHistory,
  isLoading,
}: {
  subscriptionOrUserId: string
  notificationHistory: NotificationHistory[] | undefined
  isLoading: boolean
}) => {
  const sortedNotificationHistory = (notificationHistory || []).sort((a, b) => {
    return (b.createdAt?.toDate()?.valueOf() ?? 0) - (a.createdAt?.toDate()?.valueOf() ?? 0);
  });

  return (
    <div className={sectionWrapper()}>
      {isLoading && (
        <LoadingOverlay />
      )}
      <div className={css({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      })}>
        <h2 className={sectionTitle()}>通知履歴</h2>
        <Link
          to='/admin/users/$subscriptionOrUserId/createNotification'
          params={{ subscriptionOrUserId }}
        >
          <button type='button' className={button()}>
            <Add />
            プッシュ通知作成
          </button>
        </Link>
      </div>
      <table className={css(
        table.raw(),
        { borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'border.secondary' }
      )}>
        <thead className={css(
          tableHead.raw(),
          { backgroundColor: 'surface.secondary' }
        )}>
          <tr>
            <th className={tableHeadCell()}>タイトル</th>
            <th className={tableHeadCell()}>内容</th>
            <th className={tableHeadCell()}>送信日時</th>
            <th className={tableHeadCell()} />
          </tr>
        </thead>
        <tbody>
          {sortedNotificationHistory.map(
            (notification) => <NotificationHistoryRow key={notification.id} history={notification} subscriptionOrUserId={subscriptionOrUserId} />
          )}
        </tbody>
      </table>
    </div>
  )
}

const BreadCrumbs = ({ user, isLoading }: { user: User, isLoading: boolean }) => {
  if (isLoading) {
    return null
  }
  if (user === undefined || user === null) {
    return null
  }
  return (
    <div className={css({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    })}>
      <Link
        to=".."
      >
        <span className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}>
          利用者検索
        </span>
      </Link>
      <span className={css({
        color: 'text.tertiary',
      })}>/</span>
      <span className={css({
        color: 'text.tertiary',
      })}>{user.user?.name || "--"}</span>
    </div>
  )
}

const Layout = () => {
  const { subscriptionOrUserId } = Route.useParams()
  const { user, notificationHistory, isLoading } = useUserInfo()
  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>
          <div className={css({ display: 'flex', alignItems: 'center', gap: '8px' })}>
            <Link to='..'>
              <ArrowBack />
            </Link>
            利用者詳細
          </div>
        </h1>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <BreadCrumbs user={user} isLoading={isLoading} />
        <BasicInfo user={user} isLoading={isLoading} />
        <SystemInfo user={user} isLoading={isLoading} />
        <PointHistory
          subscriptionOrUserId={subscriptionOrUserId}
          transactionGroups={user?.transactionGroups ?? []}
          isLoading={isLoading}
        />
        <NotificationHistoryTable
          subscriptionOrUserId={subscriptionOrUserId}
          notificationHistory={notificationHistory}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}
